import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import getCaretCoordinates from 'textarea-caret';
import { offset, flip, useFloating, useDismiss, useRole, useInteractions, autoUpdate } from '@floating-ui/react';
import { getOppositePlacement } from '@floating-ui/utils';
import { DictType, ScenarioAutocomplete, ScenarioAutocompletes } from "@/model";
import { VirtualList, VirtualListRef } from "@/component/VirtualList";


const ITEM_HEIGHT = 33;
const VISIBLE_ITEMS = 6;

interface InputAutocompleteMenuProps {
    targetRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement>;
    autocompletes: DictType<ScenarioAutocompletes>;
    targetText: string;
    onSelect: (value: string) => void;
}

const InputAutocompleteMenu: React.FC<InputAutocompleteMenuProps> = (props) => {
    const { targetRef, autocompletes, targetText, onSelect } = props;

    const intl = useIntl();

    const triggers = useMemo(() => Object.keys(autocompletes), [autocompletes]);

    const [activeTrigger, setActiveTrigger] = useState<string>("");
    const [keyword, setKeyword] = useState<string>("");
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const menuRef = useRef<VirtualListRef>(null);
    const triggeredCaretIdxRef = useRef<number>(-1);

    const suggestions = useMemo(() => autocompletes[activeTrigger]?.autocompletes ?? [], [activeTrigger, autocompletes]);
    const filteredSuggestions = useMemo(() => (
        suggestions.filter(suggestion => suggestion.value.includes(keyword.replace(/^\{+/, "")))
    ), [suggestions, keyword]);

    const { refs, floatingStyles, context } = useFloating({
        open: !!activeTrigger,
        onOpenChange: (open) => {
            if (!open) {
                closeMenu();
            }
        },
        placement: "bottom-start",
        strategy: "absolute",
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(4),
            flip({ fallbackPlacements: [getOppositePlacement("bottom-start")] })
        ],
    });
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const { getFloatingProps } = useInteractions([ dismiss, role ]);

    const setReference = useCallback((caretIdx: number) => {
        if (!targetRef.current) {
            return;
        }
        refs.setReference({
            ...targetRef.current,
            getBoundingClientRect: () => {
                const rect = targetRef.current!.getBoundingClientRect();
                const caretCoordinates = getCaretCoordinates(targetRef.current!, caretIdx);
                return {
                    ...rect,
                    top: rect.top + caretCoordinates.top,
                    left: rect.left + caretCoordinates.left,
                    width: 1,
                    height: caretCoordinates.height,
                };
            },
        });
    }, [targetRef, refs]);

    const openMenu = useCallback((trigger: string) => {
        if (!targetRef.current) {
            return;
        }
        const triggeredCaretIdx = targetRef.current.selectionStart;
        if (triggeredCaretIdx === -1 || triggeredCaretIdx === null) {
            return;
        }
        setActiveTrigger(trigger);
        triggeredCaretIdxRef.current = triggeredCaretIdx;
        setReference(triggeredCaretIdx);
        setShowMenu(true);
    }, [targetRef, setReference]);

    const closeMenu = useCallback(() => {
        setShowMenu(false);
        setActiveTrigger("");
        setKeyword("");
        setActiveIndex(0);
        triggeredCaretIdxRef.current = -1;
    }, []);

    const selectSuggestion = useCallback((suggestion: ScenarioAutocomplete) => {
        const triggeredCaretIdx = triggeredCaretIdxRef.current;
        onSelect(targetText.slice(0, triggeredCaretIdx) + suggestion.output + targetText.slice(triggeredCaretIdx + 1 + keyword.length));

        const nextCaretPosition = triggeredCaretIdx + suggestion.output.length;
        closeMenu();
        setTimeout(() => {
            targetRef.current?.setSelectionRange(nextCaretPosition, nextCaretPosition);
        }, 0);
    }, [targetText, keyword, onSelect, closeMenu, targetRef]);

    useEffect(() => {
        const onKeyDown = (e: React.KeyboardEvent) => {
            if (!showMenu) {
                if (triggers.includes(e.key)) {
                    openMenu(e.key);
                }
                return;
            }

            if (filteredSuggestions.length === 0) {
                return;
            }
            switch (e.key) {
                case 'Enter':
                    e.preventDefault();
                    if (activeIndex === null) {
                        return;
                    }
                    selectSuggestion(filteredSuggestions[activeIndex]);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    const downIndex = ((activeIndex ?? -1) + 1) % filteredSuggestions.length;
                    setActiveIndex(downIndex);
                    menuRef.current?.scrollTo(downIndex);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    const upIndex = (((activeIndex ?? 0) - 1 + filteredSuggestions.length) % filteredSuggestions.length);
                    setActiveIndex(upIndex);
                    menuRef.current?.scrollTo(upIndex);
                    break;
            }
        };
        const target = targetRef.current;

        target?.addEventListener('keydown', onKeyDown as any);
        return () => {
            target?.removeEventListener('keydown', onKeyDown as any);
        };
      }, [targetRef, showMenu, filteredSuggestions, activeIndex, selectSuggestion, triggers, openMenu]);

    useEffect(() => {
        if (!showMenu || !targetRef.current) {
            return;
        }

        const caretPosition = targetRef.current.selectionStart;
        const triggeredCaretIdx = triggeredCaretIdxRef.current;
        if (caretPosition === null || targetText[triggeredCaretIdx] !== activeTrigger) {
            closeMenu();
            return;
        }
        const keyword = targetText.slice(triggeredCaretIdx + 1, caretPosition);
        if (keyword.includes(' ')) {
            closeMenu();
            return;
        }
        setKeyword(keyword);
        setActiveIndex(0);
        menuRef.current?.scrollTo(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetText]);

    if (!showMenu) {
        return null;
    }

    return (
        <ul ref={refs.setFloating}
            {...getFloatingProps()}
            role="listbox"
            className="w-[280px] whitespace-wrap bg-white border rounded shadow z-10"
            style={{
                ...floatingStyles,
                maxHeight: `${VISIBLE_ITEMS * ITEM_HEIGHT}px`
            }}
        >
            {filteredSuggestions.length ? (
                <VirtualList ref={menuRef}
                             items={filteredSuggestions}
                             itemHeight={ITEM_HEIGHT}
                             visibleItems={VISIBLE_ITEMS}
                             getItemKey={(item) => item.value}
                             renderItem={(suggestion, index) => (
                                 <li role="option"
                                     aria-selected={index === activeIndex}
                                     className="w-full px-2 py-1 truncate cursor-pointer aria-selected:bg-blue-100 aria-selected:text-blue-800"
                                     onMouseDown={() => selectSuggestion(suggestion)}
                                     onMouseEnter={() => setActiveIndex(index)}
                                 >
                                     {suggestion.value}
                                 </li>
                             )}
                />
            ) : (
                <li className="px-2 py-1 whitespace-pre-wrap">
                    {(suggestions.length === 0 && autocompletes[activeTrigger]?.errorText) || intl.formatMessage({ id: "i00034" })}
                </li>
            )}
        </ul>
    );
};

export default InputAutocompleteMenu;
